import { LIBRARY_SLUG_PATTERN, libraryRightRouteSchema } from "./library-rights.js";
import { LIBRARY_INSTANCE_SLUG_PATTERN, isLibraryInstanceSlug } from "./library-instance-slug.js";
import { isLibraryEventTopic } from "./library-topics.js";
import { LIBRARY_USE_CONFIG_MAX_BYTES, isLibraryConfigWithinBounds } from "./library-use.js";
import { z } from "zod";
//#region src/library/library-access.ts
/**
* The three files the operator program mounts for a library instance and its consumer, and the
* answer of the core-api consent and version lookup (2026-10-12-shared-libraries, I8, R-005,
* R-007, R-012, R-020, R-023, R-026, R-030, R-032, R-038, R-039).
*
* No org id appears in any file of this module (R-039 item 1). A file holds per-version data only.
* The per-org facts (has this org consented, and which consumer version and library version does it
* run) come from core-api at call time, through {@link LIBRARY_CONSENT_LOOKUP_PATH_PREFIX}, for the
* org of the verified token that the calling software carried (R-038, R-039 item 2).
*
* 1. `/etc/zion/library-access.json`, in each instance pod ({@link LibraryAccessFile}): for each
*    pair of a live consumer version and a live library version, the routes and the inbound topics
*    that the verified `lrt` of that consumer version allows on that library version. The SDK guard
*    and the SDK event consumer in the instance read it (WS-10, WS-11, WS-21). The operator writes
*    it (WS-17).
* 2. `/etc/zion/libraries/<slug>.json`, in each pod of the consumer ({@link LibraryVersionMapFile}):
*    the library slug, the instance slug, and for each live library version its outbound topics.
*    The SDK library client reads the instance slug from it, and the consumer's event consumer and
*    event bridge check an outbound event against it (WS-10, WS-20, WS-21).
* 3. `/etc/zion/consumer-config.json`, in each instance pod ({@link ConsumerConfigFile}): the
*    consumer's values for the library's `consumerConfig`, one value per consumer (I7, I8,
*    R-039 item 3).
*
* A consumer here is a consuming app, or the instance of a library that uses another library. Its
* slug is then a `zl-` slug (R-033).
*
* The decision functions state the order of the refusals once, so the Node and the Python SDKs give
* the same answer (R-032). Pure and browser-safe. No schema uses `.transform()`.
*/
/** The path of the access file in each instance pod (R-007). */
const LIBRARY_ACCESS_FILE_PATH = "/etc/zion/library-access.json";
/** The path of the consumer config file in each instance pod (I8). */
const CONSUMER_CONFIG_FILE_PATH = "/etc/zion/consumer-config.json";
/** The directory of the version map files in each pod of a consumer (R-023). */
const LIBRARY_VERSION_MAP_DIRECTORY = "/etc/zion/libraries";
/** The format version every file of this module carries in `v`. */
const LIBRARY_FILE_FORMAT_VERSION = 1;
/**
* The most entries one file holds. In an access file an entry is one pair of a live consumer version
* and a live library version, so the bound is on the pairs: C live consumer versions and L live
* library versions give up to C x L entries, and 8 x 8 is the largest square set that fits. In a
* version map an entry is one live library version. A data plane runs one version per ring and per
* accepted private channel on each side. The operator program refuses a file over the bound with
* `too_many_entries` and writes no new file (R-050 item 1). An instance with no live file then
* mounts none, so each of its guards answers 403 `route_not_allowed`. An instance with a live file
* keeps that file until a plan passes (R-047 item 5). The waypoint allow rules still follow the
* verified rights, so a revoked right gets 403 at the waypoint either way (R-054).
*/
const LIBRARY_FILE_ENTRIES_MAX = 64;
/** The most routes one entry of an access file allows: every route of every right of one library. */
const LIBRARY_ACCESS_ROUTES_MAX = 1024;
/**
* The longest time, in seconds, that a caller of the consent and version lookup may keep one
* answer (R-039 item 2). So a revoke reaches the SDK guard, the SDK event consumers and the event
* bridge within 30 seconds.
*/
const LIBRARY_CONSENT_CACHE_MAX_SECONDS = 30;
/**
* The path prefix of the core-api consent and version lookup. The whole path is
* `/internal/v1/library-instances/<instance slug>/consent`; build it with
* {@link libraryConsentLookupPath}. core-api serves it (WS-9). The caller sends its service bearer
* and the org-bound token in `x-zion-end-user-token`; core-api takes the org from that verified
* token alone (DEF-10, R-039 item 2).
*/
const LIBRARY_CONSENT_LOOKUP_PATH_PREFIX = "/internal/v1/library-instances";
const APP_SLUG_PATTERN = /^[a-z][a-z0-9-]*[a-z0-9]$/;
const VERSION_PATTERN = /^\d+\.\d+\.\d+/;
const VERSION_MAX_LENGTH = 64;
const appSlugSchema = z.string().max(30).regex(APP_SLUG_PATTERN);
const librarySlugSchema = z.string().max(30).regex(LIBRARY_SLUG_PATTERN);
const instanceSlugSchema = z.string().max(30).regex(LIBRARY_INSTANCE_SLUG_PATTERN);
const versionSchema = z.string().max(VERSION_MAX_LENGTH).regex(VERSION_PATTERN);
const topicListSchema = z.array(z.string().max(63).refine(isLibraryEventTopic, { message: "not a library event topic" })).max(32).refine((topics) => new Set(topics).size === topics.length, { message: "topics must be unique" });
/** The zod form of {@link LibraryAccessFile}. Strict at every level. */
const libraryAccessFileSchema = z.strictObject({
	v: z.literal(1),
	librarySlug: librarySlugSchema,
	instanceSlug: instanceSlugSchema,
	consumerSlug: appSlugSchema,
	entries: z.array(z.strictObject({
		consumerVersion: versionSchema,
		libraryVersion: versionSchema,
		routes: z.array(libraryRightRouteSchema).max(LIBRARY_ACCESS_ROUTES_MAX),
		inboundTopics: topicListSchema
	})).max(64).refine((entries) => new Set(entries.map((entry) => `${entry.consumerVersion} ${entry.libraryVersion}`)).size === entries.length, { message: "two entries name the same consumer version and library version" })
});
/** The zod form of {@link LibraryVersionMapFile}. Strict at every level. */
const libraryVersionMapFileSchema = z.strictObject({
	v: z.literal(1),
	librarySlug: librarySlugSchema,
	instanceSlug: instanceSlugSchema,
	versions: z.array(z.strictObject({
		libraryVersion: versionSchema,
		outboundTopics: topicListSchema
	})).max(64).refine((versions) => new Set(versions.map((entry) => entry.libraryVersion)).size === versions.length, { message: "two entries name the same library version" })
});
/**
* The zod form of {@link ConsumerConfigFile}. Strict at the root; `values` nests at most 32 containers
* and is at most 65,536 bytes of JSON ({@link isLibraryConfigWithinBounds}).
*/
const consumerConfigFileSchema = z.strictObject({
	v: z.literal(1),
	librarySlug: librarySlugSchema,
	consumerSlug: appSlugSchema,
	values: z.record(z.string(), z.unknown()).refine(isLibraryConfigWithinBounds, { message: `values exceed 32 levels or ${LIBRARY_USE_CONFIG_MAX_BYTES} bytes` })
});
/**
* The zod form of {@link LibraryConsentAnswer}: the response body of the lookup. Strict in both arms,
* so a `consented: false` answer that carries a version, or an answer that carries an org id, fails.
*/
const libraryConsentAnswerSchema = z.discriminatedUnion("consented", [z.strictObject({
	consented: z.literal(true),
	consumerVersion: versionSchema,
	libraryVersion: versionSchema
}), z.strictObject({ consented: z.literal(false) })]);
function parseWith(schema, value) {
	const parsed = schema.safeParse(value);
	if (parsed.success) return {
		ok: true,
		file: parsed.data
	};
	return {
		ok: false,
		issues: parsed.error.issues.map((issue) => issue.path.length === 0 ? "(root)" : issue.path.map(String).join("."))
	};
}
/** Parses untrusted access file content with {@link libraryAccessFileSchema}. Never throws. Pure. */
function parseLibraryAccessFile(value) {
	return parseWith(libraryAccessFileSchema, value);
}
/** Parses untrusted version map content with {@link libraryVersionMapFileSchema}. Never throws. Pure. */
function parseLibraryVersionMapFile(value) {
	return parseWith(libraryVersionMapFileSchema, value);
}
/** Parses untrusted consumer config content with {@link consumerConfigFileSchema}. Never throws. Pure. */
function parseConsumerConfigFile(value) {
	return parseWith(consumerConfigFileSchema, value);
}
/**
* Parses an untrusted lookup response body with {@link libraryConsentAnswerSchema}. Never throws.
* A failed parse is a lookup fault, never `consented: false` (R-047 item 7, R-045 item 2): the
* guard answers 503 `consent_unavailable`, an event gate leaves the event unacked for redelivery,
* and no caller caches the fault. Pure.
*/
function parseLibraryConsentAnswer(value) {
	return parseWith(libraryConsentAnswerSchema, value);
}
/**
* The path `/etc/zion/libraries/<slug>.json` of the version map of `librarySlug`. It throws
* `TypeError` for a slug off the library slug grammar or over 30 characters. Pure.
*/
function libraryVersionMapFilePath(librarySlug) {
	if (librarySlug.length > 30 || !LIBRARY_SLUG_PATTERN.test(librarySlug)) throw new TypeError(`libraryVersionMapFilePath: ${JSON.stringify(librarySlug)} is not a library slug`);
	return `${LIBRARY_VERSION_MAP_DIRECTORY}/${librarySlug}.json`;
}
/**
* The path `/internal/v1/library-instances/<instance slug>/consent` of the lookup for one instance.
* It throws `TypeError` for a value that is not an instance slug ({@link isLibraryInstanceSlug}), so
* no caller text other than a slug reaches the path. Pure.
*/
function libraryConsentLookupPath(instanceSlug) {
	if (!isLibraryInstanceSlug(instanceSlug)) throw new TypeError(`libraryConsentLookupPath: ${JSON.stringify(instanceSlug)} is not an instance slug`);
	return `${LIBRARY_CONSENT_LOOKUP_PATH_PREFIX}/${instanceSlug}/consent`;
}
/**
* The `error` code of the 403 that core-api's consent lookup ({@link libraryConsentLookupPath})
* sends for a forwarded token that names no verified org-bound user. core-api sends it when the
* token is absent, over 8,192 bytes, no compact JWS, does not verify (an expired token, a token of
* another key and an empty signature included), names no UUID `orgId`, or names no end user (a
* service bearer, an MCP session token or an outside caller).
*
* The body is `{ error: LIBRARY_CONSENT_TOKEN_REFUSED, message: '<code>: <detail>' }`. The answer is
* final for that token: a later try gives the same answer. So every SDK reads it as a refusal of the
* token (`no_org_token`), never as a lookup fault (R-047 item 7, R-049 item 4). core-api is the
* producer; the `@zion/sdk-node` lookup, the `@zion/sdk-nestjs` guard runtime and the Python SDK
* read it. The Python SDK declares the same text in `zion_sdk.library.consent`, and
* `zion_sdk.library.testing` re-exports it.
*/
const LIBRARY_CONSENT_TOKEN_REFUSED = "library_consent_token_refused";
/**
* The `error` code of the 403 that core-api's consent lookup sends for a caller that may not ask
* about the instance: a bearer that is no service fixture principal, an instance with no link, or a
* caller that is neither the instance nor its consumer. The body has the shape of
* {@link LIBRARY_CONSENT_TOKEN_REFUSED}. Every SDK reads it as the lookup fault `status`, because an
* instance link can appear on a later try.
*/
const LIBRARY_CONSENT_CALLER_REFUSED = "library_consent_caller_refused";
function accessEntryOf(file, consumerVersion, libraryVersion) {
	return file.entries.find((entry) => entry.consumerVersion === consumerVersion && entry.libraryVersion === libraryVersion);
}
/**
* The decision of the instance's SDK guard on one call (R-005, R-007, R-012, R-020, R-023, R-039).
* `consent` is the lookup answer for `facts.orgId`, or `null` when no lookup ran because no org is
* present. A lookup that failed, or a body that failed {@link parseLibraryConsentAnswer}, is a
* fault and never `consented: false` (R-047 item 7): the guard answers 503 `consent_unavailable`
* and does not call this function. The checks run in this order, and the first failure gives the
* answer:
*
* 1. `callerAppId` equals `file.consumerSlug`: else 403 `wrong_caller`;
* 2. an org is present: else 403 `no_org_token`;
* 3. `consent` is present and reads `consented: true`: else 403 `org_not_consented`;
* 4. the answer's `libraryVersion` equals `servedVersion`: else 421 `wrong_version`;
* 5. the entry for the answer's `consumerVersion` and `servedVersion` holds a route that names
*    `fixture` and `routePath`, and whose `methods` are absent or hold `method`: else 403
*    `route_not_allowed`. A pair with no entry allows nothing.
*
* Pure.
*/
function decideLibraryCall(file, facts, consent) {
	if (facts.callerAppId !== file.consumerSlug) return {
		ok: false,
		status: 403,
		reason: "wrong_caller"
	};
	if (facts.orgId === null) return {
		ok: false,
		status: 403,
		reason: "no_org_token"
	};
	if (consent === null || !consent.consented) return {
		ok: false,
		status: 403,
		reason: "org_not_consented"
	};
	if (consent.libraryVersion !== facts.servedVersion) return {
		ok: false,
		status: 421,
		reason: "wrong_version"
	};
	const entry = accessEntryOf(file, consent.consumerVersion, facts.servedVersion);
	if (!(entry !== void 0 && entry.routes.some((route) => route.fixture === facts.fixture && route.path === facts.routePath && (route.methods === void 0 || route.methods.includes(facts.method))))) return {
		ok: false,
		status: 403,
		reason: "route_not_allowed"
	};
	return { ok: true };
}
/**
* The decision on one library event, from the lookup answer for the verified envelope org and the
* topic (R-026, R-030, R-039). `consent` is `null` when no answer exists. For an inbound event at the
* instance, pass the access file; the topic list is the `inboundTopics` of the entry for the
* answer's `consumerVersion` and `libraryVersion`. For an outbound event at the consumer, pass the
* version map; the topic list is the `outboundTopics` of the entry for the answer's
* `libraryVersion`. The checks run in this order:
*
* 1. `consent` is present and reads `consented: true`: else `org_not_consented`;
* 2. the entry exists and its topic list holds `topic`: else `topic_not_allowed`.
*
* The consumer drops a refused event and acknowledges it. Pure.
*/
function decideLibraryEvent(file, consent, topic) {
	if (consent === null || !consent.consented) return {
		ok: false,
		reason: "org_not_consented"
	};
	const topics = "entries" in file ? accessEntryOf(file, consent.consumerVersion, consent.libraryVersion)?.inboundTopics : file.versions.find((entry) => entry.libraryVersion === consent.libraryVersion)?.outboundTopics;
	return topics !== void 0 && topics.includes(topic) ? { ok: true } : {
		ok: false,
		reason: "topic_not_allowed"
	};
}
//#endregion
export { CONSUMER_CONFIG_FILE_PATH, LIBRARY_ACCESS_FILE_PATH, LIBRARY_ACCESS_ROUTES_MAX, LIBRARY_CONSENT_CACHE_MAX_SECONDS, LIBRARY_CONSENT_CALLER_REFUSED, LIBRARY_CONSENT_LOOKUP_PATH_PREFIX, LIBRARY_CONSENT_TOKEN_REFUSED, LIBRARY_FILE_ENTRIES_MAX, LIBRARY_FILE_FORMAT_VERSION, LIBRARY_VERSION_MAP_DIRECTORY, consumerConfigFileSchema, decideLibraryCall, decideLibraryEvent, libraryAccessFileSchema, libraryConsentAnswerSchema, libraryConsentLookupPath, libraryVersionMapFilePath, libraryVersionMapFileSchema, parseConsumerConfigFile, parseLibraryAccessFile, parseLibraryConsentAnswer, parseLibraryVersionMapFile };

//# sourceMappingURL=library-access.js.map